import * as React from 'react';
import { Highlight, themes } from 'prism-react-renderer';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

function niceLang(raw) {
  const lang = (raw || '').toLowerCase();
  if (lang === 'js') return 'JavaScript';
  if (lang === 'ts') return 'TypeScript';
  if (lang === 'tsx') return 'TSX';
  if (lang === 'jsx') return 'JSX';
  if (lang === 'sh') return 'Shell';
  return lang || 'text';
}

export default function CodeBlock({ className, children }) {
  const code = String(children || '').replace(/\n$/, '');
  const match = /language-([\w-]+)/.exec(className || '');
  const lang = match?.[1] || 'text';
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } finally {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <Box sx={{
      my: 2,
      border: '1px solid #333',
      borderRadius: 2,
      overflow: 'hidden',
    }}>
      {/* Header bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        py: 0.3,
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'grey',
        fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace)'
      }}>
        <Box component="span" sx={{ textTransform: 'lowercase', letterSpacing: .5, fontSize: '10px' }}>
          {niceLang(lang)}
        </Box>
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton size="small" onClick={handleCopy} aria-label="Copy code" sx={{ color: '#bbb' }}>
            {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Highlighted code */}
      <Highlight theme={themes.oneLight} code={code} language={lang}>
        {({ className: c, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={c}
            style={{ ...style, margin: 0, padding: 12, fontSize: 13, overflowX: 'auto', tabSize: 2 }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </Box>
  );
}

